// ACTA_AGENT_GMAO_ENTITY_PREPARATION_V1
// ACTA_AGENT_GMAO_WRITE_PREPARATION_V1
// ACTA_AGENT_GMAO_ASSIGN_RESOLUTION_V1

import {
  agent as agentApi,
} from "@/api";


function clean(value = "") {
  return String(value || "").trim();
}


function normalizedResolution(response, entityType) {
  const status =
    String(
      response?.status || "NOT_FOUND"
    ).toUpperCase();

  const matches =
    Array.isArray(response?.matches)
      ? response.matches
      : [];

  const selected =
    response?.entity
    || (
      status === "RESOLVED"
      && matches.length === 1
        ? matches[0]
        : null
    );

  return {
    status,
    entityType,
    matches,
    selected,
    requiresConfirmation:
      status === "AMBIGUOUS",
  };
}


export async function prepareAgentMachine({
  machineQuery = "",
  machineId = "",
} = {}) {
  const knownId = clean(machineId);

  if (knownId) {
    return {
      status: "RESOLVED",
      entityType: "machine",
      matches: [{ id: knownId }],
      selected: { id: knownId },
      requiresConfirmation: false,
    };
  }

  const query = clean(machineQuery);
  if (!query) {
    return {
      status: "NOT_FOUND",
      entityType: "machine",
      matches: [],
      selected: null,
      requiresConfirmation: false,
    };
  }

  return normalizedResolution(
    await agentApi.resolveMachine(query),
    "machine"
  );
}


export async function prepareAgentIntervention({
  interventionQuery = "",
  interventionId = "",
} = {}) {
  const knownId = clean(interventionId);

  if (knownId) {
    return {
      status: "RESOLVED",
      entityType: "intervention",
      matches: [{ id: knownId }],
      selected: { id: knownId },
      requiresConfirmation: false,
    };
  }

  const query = clean(interventionQuery);
  if (!query) {
    return {
      status: "NOT_FOUND",
      entityType: "intervention",
      matches: [],
      selected: null,
      requiresConfirmation: false,
    };
  }

  return normalizedResolution(
    await agentApi.resolveIntervention(query),
    "intervention"
  );
}


export async function prepareAgentTechnician({
  technicianQuery = "",
} = {}) {
  const query = clean(technicianQuery);

  if (!query) {
    return {
      status: "NOT_FOUND",
      entityType: "technician",
      matches: [],
      selected: null,
      requiresConfirmation: false,
    };
  }

  return normalizedResolution(
    await agentApi.resolveTechnician(query),
    "technician"
  );
}


const WRITE_ENTITY_KIND = Object.freeze({
  "intervention.create": "machine",
  "intervention.update": "intervention",
  "intervention.transition": "intervention",
  "intervention.cancel": "intervention",
  "intervention.assign": "intervention",
});


function withResolvedEntityId(
  actionId,
  params,
  entity
) {
  const input = {
    ...(params || {}),
  };

  delete input.machineQuery;
  delete input.interventionQuery;
  delete input.technicianQuery;
  delete input.unassign;

  if (
    actionId ===
    "intervention.create"
  ) {
    input.machineId =
      clean(entity?.id);
    delete input.interventionId;
  } else {
    input.interventionId =
      clean(entity?.id);
    delete input.machineId;
  }

  return input;
}


function technicianDisplayName(
  technician = {}
) {
  return clean(
    [
      technician.firstName,
      technician.lastName,
    ]
      .filter(Boolean)
      .join(" ")
  )
    || clean(technician.email)
    || clean(technician.id)
    || "Technicien";
}


function readyAssignment(
  params,
  intervention,
  technician
) {
  const baseInput =
    withResolvedEntityId(
      "intervention.assign",
      params,
      intervention
    );

  return {
    status: "RESOLVED",
    phase: "ready",
    entityType: "intervention",
    matches: [intervention],
    selected: intervention,
    intervention,
    technician: technician || null,
    input: {
      ...baseInput,
      technicianId:
        technician?.id
          ? clean(technician.id)
          : null,
    },
  };
}


export async function prepareAgentInterventionAssign({
  params = {},
} = {}) {
  const interventionResolution =
    await prepareAgentIntervention({
      interventionQuery:
        params?.interventionQuery
        || "",
      interventionId:
        params?.interventionId
        || "",
    });

  if (
    interventionResolution.status !==
      "RESOLVED"
    || !interventionResolution.selected?.id
  ) {
    return {
      status:
        interventionResolution.status,
      phase: "intervention",
      entityType: "intervention",
      matches:
        interventionResolution.matches
        || [],
      selected:
        interventionResolution.selected
        || null,
      input: null,
      technician: null,
    };
  }

  if (params?.unassign === true) {
    return readyAssignment(
      params,
      interventionResolution.selected,
      null
    );
  }

  const technicianResolution =
    await prepareAgentTechnician({
      technicianQuery:
        params?.technicianQuery
        || "",
    });

  if (
    technicianResolution.status !==
      "RESOLVED"
    || !technicianResolution.selected?.id
  ) {
    return {
      status:
        technicianResolution.status,
      phase: "technician",
      entityType: "technician",
      matches:
        technicianResolution.matches
        || [],
      selected:
        technicianResolution.selected
        || null,
      intervention:
        interventionResolution.selected,
      technician: null,
      input: null,
    };
  }

  return readyAssignment(
    params,
    interventionResolution.selected,
    technicianResolution.selected
  );
}


export async function continueAgentInterventionAssign({
  params = {},
  prepared = {},
  selected,
} = {}) {
  if (!selected?.id) {
    throw new Error(
      "Sélection Agent GMAO invalide."
    );
  }

  if (prepared?.phase === "intervention") {
    return prepareAgentInterventionAssign({
      params: {
        ...(params || {}),
        interventionId: clean(selected.id),
        interventionQuery: "",
      },
    });
  }

  if (
    prepared?.phase === "technician"
    && prepared?.intervention?.id
  ) {
    return readyAssignment(
      params,
      prepared.intervention,
      selected
    );
  }

  throw new Error(
    "Étape de résolution Agent GMAO invalide."
  );
}


export async function prepareAgentGmaoWrite({
  actionId = "",
  params = {},
} = {}) {
  if (
    actionId ===
    "intervention.assign"
  ) {
    return prepareAgentInterventionAssign({
      params,
    });
  }

  const entityType =
    WRITE_ENTITY_KIND[actionId];

  if (!entityType) {
    throw new Error(
      `Action GMAO non supportée pour confirmation: ${actionId}`
    );
  }

  const resolution =
    entityType === "machine"
      ? await prepareAgentMachine({
          machineQuery:
            params?.machineQuery
            || "",
          machineId:
            params?.machineId
            || "",
        })
      : await prepareAgentIntervention({
          interventionQuery:
            params?.interventionQuery
            || "",
          interventionId:
            params?.interventionId
            || "",
        });

  return {
    ...resolution,
    actionId,
    input:
      resolution.selected?.id
        ? withResolvedEntityId(
            actionId,
            params,
            resolution.selected
          )
        : null,
  };
}


export function bindAgentGmaoWriteEntity(
  actionId,
  params,
  entity
) {
  return withResolvedEntityId(
    actionId,
    params,
    entity
  );
}


export function bindAgentTechnician(
  input = {},
  technician
) {
  return {
    ...(input || {}),
    technicianId:
      technician?.id
        ? clean(technician.id)
        : null,
  };
}


export async function executeConfirmedAgentGmaoWrite({
  actionId,
  input,
}) {
  return agentApi.execute({
    actionId,
    input,
    confirmed: true,
  });
}


export function agentGmaoWriteSummary(
  actionId,
  input = {},
  entity = {},
  technician = null
) {
  const entityLabel =
    clean(
      entity.title
      || entity.name
      || entity.model
      || entity.serialNumber
      || entity.id
    );

  if (
    actionId ===
    "intervention.create"
  ) {
    return [
      input.title
        ? `Créer l’intervention « ${input.title} »`
        : "Créer une intervention",
      entityLabel
        ? `Machine : ${entityLabel}`
        : "",
      input.interventionType
        ? `Type : ${input.interventionType}`
        : "",
      input.priority
        ? `Priorité : ${input.priority}`
        : "",
      input.scheduledStart
        ? `Début : ${input.scheduledStart}`
        : "",
    ].filter(Boolean);
  }

  if (
    actionId ===
    "intervention.transition"
  ) {
    return [
      `Changer le statut en « ${input.status || ""} »`,
      entityLabel
        ? `Intervention : ${entityLabel}`
        : "",
      input.note
        ? `Note : ${input.note}`
        : "",
    ].filter(Boolean);
  }

  if (
    actionId ===
    "intervention.cancel"
  ) {
    return [
      "Annuler l’intervention",
      entityLabel
        ? `Intervention : ${entityLabel}`
        : "",
      input.reason
        ? `Motif : ${input.reason}`
        : "",
    ].filter(Boolean);
  }

  if (
    actionId ===
    "intervention.assign"
  ) {
    return [
      input.technicianId
        ? "Affecter l’intervention"
        : "Retirer le technicien affecté",
      entityLabel
        ? `Intervention : ${entityLabel}`
        : "",
      input.technicianId
        ? `Technicien : ${
            technicianDisplayName(
              technician
            )
          }`
        : "Aucun technicien ne sera affecté",
    ].filter(Boolean);
  }

  if (
    actionId ===
    "intervention.update"
  ) {
    const changes = [
      input.title
        ? `Titre : ${input.title}`
        : "",
      input.interventionType
        ? `Type : ${input.interventionType}`
        : "",
      input.priority
        ? `Priorité : ${input.priority}`
        : "",
      input.description
        ? `Description : ${input.description}`
        : "",
      input.scheduledStart
        ? `Début : ${input.scheduledStart}`
        : "",
      input.scheduledEnd
        ? `Fin : ${input.scheduledEnd}`
        : "",
    ].filter(Boolean);

    return [
      "Modifier l’intervention",
      entityLabel
        ? `Intervention : ${entityLabel}`
        : "",
      ...changes,
    ].filter(Boolean);
  }

  return [];
}


export function agentMachinePath(machine) {
  const id = clean(machine?.id);
  return id
    ? `/app/gmao/v2/parc/${encodeURIComponent(id)}`
    : "";
}


export function agentInterventionPath(intervention) {
  const id = clean(intervention?.id);
  return id
    ? `/app/gmao/interventions/${encodeURIComponent(id)}`
    : "";
}
